import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import * as Icons from "lucide-react";

const NAVY = "#1C4358", RED = "#9C1E22", GREEN = "#2F7D5A", GOLD = "#B7791F";

const Stat = ({ icon: Icon, label, value, color }) => (
  <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="marketing-stat">
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A` }}><Icon size={19} style={{ color }} /></span>
      <div className="min-w-0"><div className="text-2xl font-bold font-display tabular-nums text-[#252221] leading-none">{value ?? 0}</div><div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div></div>
    </div>
  </Card>
);

const MODULES = [
  { to: "/marketing/analytics", icon: "LineChart", title: "Website Analytics", desc: "Pageviews, sources & conversions from your site.", color: NAVY },
  { to: "/marketing/forms", icon: "FormInput", title: "Landing-Page Forms", desc: "Capture leads from your website with embeddable forms.", color: RED },
  { to: "/marketing/subscribers", icon: "Users", title: "Subscribers", desc: "Build your audience from leads, families & partners.", color: GREEN },
  { to: "/marketing/newsletters", icon: "Mail", title: "Newsletter", desc: "Draft with AI, manage audience, export to send.", color: GOLD },
  { to: "/marketing/automations", icon: "Workflow", title: "Automations", desc: "Auto follow-ups, assignment & nurture sequences.", color: "#6B46C1" },
];

const Mod = ({ name, ...p }) => { const C = Icons[name] || Icons.Square; return <C {...p} />; };

export default function MarketingOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const res = await api.get("/marketing/overview"); setData(res.data); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="marketing-overview">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#252221]">Marketing &amp; Growth</h1>
        <p className="text-sm text-muted-foreground">Acquire, nurture, and automate — connected to your CRM.</p>
      </div>

      {loading ? <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div> : (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Stat icon={Icons.Workflow} label="Active automations" value={data?.active_automations} color={"#6B46C1"} />
          <Stat icon={Icons.Users} label="Subscribers" value={data?.subscribers} color={GREEN} />
          <Stat icon={Icons.Eye} label="Pageviews (7d)" value={data?.pageviews_7d} color={NAVY} />
          <Stat icon={Icons.MousePointerClick} label="Conversions (7d)" value={data?.conversions_7d} color={RED} />
          <Stat icon={Icons.FormInput} label="Forms" value={data?.forms} color={GOLD} />
          <Stat icon={Icons.Inbox} label="Submissions" value={data?.submissions} color={"#252221"} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MODULES.map((m) => (
          <button key={m.to} onClick={() => navigate(m.to)} className="text-left" data-testid={`marketing-module-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Card className="p-5 border-[#E6EDF2] h-full hover:bg-[rgba(155,185,206,0.07)] transition-colors" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
              <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${m.color}1A` }}><Mod name={m.icon} size={22} style={{ color: m.color }} /></div>
              <div className="font-display font-semibold text-[#252221] flex items-center gap-1.5">{m.title} <Icons.ArrowRight size={15} className="text-muted-foreground" /></div>
              <div className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{m.desc}</div>
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}
